import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import agent from "../../../app/api/agent";
import { Button, Item, Modal } from "semantic-ui-react";
import { Application } from "../../applications/types";
import ApplicationListItem from "../../applications/components/ApplicationListItem";
import Loader from "../../../components/Loader";
import { Link } from "react-router-dom";
import { ApplicationStatus } from "../../../app/enums/applicationStatus";
import { isExistingReflection } from "../../../app/helpers/reflections";
import ReturnForEditModal from "./ReturnForEditModal";

interface IProps {
  studentId: number;
  applicationId: number;
  studentFullName: string;
  grade: string;
  handleCancel(applicationId: number): void;
  handleReturn?(applicationId: number): void;
}

const StudentPendingApplicationModal = ({
  studentId,
  applicationId,
  studentFullName,
  grade,
  handleCancel,
  handleReturn,
}: IProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [application, setApplication] = useState<Application>();
  const [returnModalOpen, setReturnModalOpen] = useState(false);

  useEffect(() => {
    if (open) {
      loadApplication();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadApplication = () => {
    setLoading(true);

    agent.Applications.getApplication(studentId, applicationId)
      .then((response) => {
        if (response) setApplication(response);
      })
      .finally(() => setLoading(false));
  };

  const allowCancelApplication = () => {
    if (!application) return false;

    const allowToCancel =
      [
        ApplicationStatus.COMPANY_APPROVED,
        ApplicationStatus.WAITING_FOR_SCHOOL_APPROVAL,
        ApplicationStatus.WAITING_FOR_COMPANY_APPROVAL,
      ].includes(application.status) && Date.parse(application.shadowing.date) > Date.now();

    return allowToCancel;
  };

  const allowReturnForEdit = () => {
    if (!application) return false;
    return (
      application.status === ApplicationStatus.WAITING_FOR_SCHOOL_APPROVAL &&
      Date.parse(application.shadowing.date) > Date.now()
    );
  };

  const handleReturned = () => {
    setReturnModalOpen(false);
    setOpen(false);
    handleReturn?.(applicationId);
  };

  const renderApplication = () => {
    if (isLoading) return <Loader />;

    return (
      <>
        {application && (
          <>
            <ApplicationListItem
              student={application.student}
              key={application.id}
              application={application}
              showReadFeedback={isExistingReflection(application.reflections)}
              allowCancelApplication={allowCancelApplication()}
              handleCancelApplication={handleCancel}
            />
            {allowReturnForEdit() && (
              <div style={{ marginTop: "1em" }}>
                <Button color="orange" size="small" onClick={() => setReturnModalOpen(true)}>
                  {t("applications.school.actions.return_for_edit")}
                </Button>
              </div>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <>
      <Modal
        dimmer="inverted"
        closeIcon
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        open={open}
        trigger={<Link to="#">{t("common.actions.view")}</Link>}
      >
        <Modal.Header>
          {studentFullName}, {grade === "Studentas" ? grade : `${grade} kl.`}
        </Modal.Header>
        <Modal.Content scrolling>
          <Item.Group>{renderApplication()}</Item.Group>
        </Modal.Content>
      </Modal>

      <ReturnForEditModal
        applicationId={returnModalOpen ? applicationId : null}
        onClose={() => setReturnModalOpen(false)}
        onReturned={handleReturned}
      />
    </>
  );
};

export default StudentPendingApplicationModal;
